// /en/marriage/$nationality/in/$location — EN mirror of triple-key combo
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { MARRIAGE_NATIONALITIES, getMarriageNationality } from "@/data/marriage-nationalities";
import { MARRIAGE_VENUES, getMarriageVenue } from "@/routes/marriage.$nationality.in.$location";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { FaqAccordion, ContactCTA } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import { Heart, ShieldCheck, BadgeCheck, FileCheck2, Clock, Phone, MessageCircle, MapPin, ArrowRight } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/marriage/$nationality/in/$location")({
  loader: ({ params }) => {
    const n = getMarriageNationality(params.nationality);
    const v = getMarriageVenue(params.location);
    if (!n || !v) throw notFound();
    return { nationality: params.nationality, location: params.location };
  },
  head: ({ params }) => {
    const n = getMarriageNationality(params.nationality);
    const v = getMarriageVenue(params.location);
    if (!n || !v) return { meta: [{ title: ctaTitle("Not found", "/en/marriage/$nationality/in/$location", "en")}] };
    const url = `${SITE}/en/marriage/${n.slug}/in/${v.slug}`;
    const title = `Marry a ${n.countryEn} citizen at ${v.nameTh} (${v.region}) ${n.flag} — from THB ${n.serviceFeeTh.toLocaleString()} | NYC`;
    const desc = `Register marriage between a ${n.countryEn} national and a Thai citizen at ${v.amphoe} (${v.nameTh}). Embassy escort, sworn Thai translation, MFA legalization, amphur booking — all included, ${n.turnaround}. Free consultation, fast reply.`;
    return {
      meta: [
        { title: ctaTitle(title, "/en/marriage/$nationality/in/$location", "en") },
        { name: "description", content: ctaDesc(desc, "/en/marriage/$nationality/in/$location", "en")},
        { name: "keywords", content: `marry ${n.countryEn} ${v.nameTh}, marriage ${n.countryEn} ${v.region} Thailand, amphur ${v.nameTh} foreign marriage` },
        { property: "og:title", content: ctaTitle(title, "/en/marriage/$nationality/in/$location", "en")},
        { property: "og:description", content: ctaDesc(desc, "/en/marriage/$nationality/in/$location", "en")},
        { property: "og:url", content: url },
        { property: "og:locale", content: "en_US" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/marriage/${n.slug}/in/${v.slug}`, `/en/marriage/${n.slug}/in/${v.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: `Marriage Registration for ${n.countryEn} at ${v.nameTh}`,
              serviceType: `Marriage Registration ${n.countryEn} × ${v.nameTh}`,
              inLanguage: "en",
              areaServed: [
                { "@type": "AdministrativeArea", name: `${v.nameTh}, ${v.region}` },
                { "@type": "Country", name: n.countryEn },
              ],
              provider: {
                "@type": "LegalService",
                "@id": `${SITE}/#org`,
                name: "NYC Legal & Notary Service",
                url: SITE,
                telephone: "+66832494999",
              },
              offers: { "@type": "Offer", price: n.serviceFeeTh, priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "@id": `${url}#faq`,
              inLanguage: "en",
              mainEntity: [
                {
                  "@type": "Question",
                  name: `Can a ${n.countryEn} national register marriage at ${v.nameTh}?`,
                  acceptedAnswer: { "@type": "Answer", text: `Yes. ${v.amphoe} accepts foreign-Thai marriage registrations including ${n.countryEn} nationals. NYC prepares the affidavit, translation, MFA legalization, and books the amphur appointment.` },
                },
                {
                  "@type": "Question",
                  name: `How long does the process take at ${v.nameTh}?`,
                  acceptedAnswer: { "@type": "Answer", text: `Approximately ${n.turnaround} including embassy affidavit + sworn translation + MFA + amphur appointment. Express 3-day service is available for +THB 3,500.` },
                },
                {
                  "@type": "Question",
                  name: `What makes ${v.nameTh} a good choice?`,
                  acceptedAnswer: { "@type": "Answer", text: v.highlights.join("; ") },
                },
                {
                  "@type": "Question",
                  name: `Will our Thai marriage be valid in ${n.countryEn}?`,
                  acceptedAnswer: { "@type": "Answer", text: n.apostilleOnly ? `Yes — translate the Kor.Ror.2 / Kor.Ror.3 into your home language and apostille it through the Thai MFA. Direct recognition in ${n.countryEn}.` : `Yes — translate and legalise the Kor.Ror.2 / Kor.Ror.3 through the Thai MFA, then attest at the ${n.countryEn} embassy in Bangkok.` },
                },
              ],
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
                { "@type": "ListItem", position: 2, name: "Marry in Thailand", item: `${SITE}/en/marriage` },
                { "@type": "ListItem", position: 3, name: n.countryEn, item: `${SITE}/en/marriage/${n.slug}` },
                { "@type": "ListItem", position: 4, name: v.nameTh, item: url },
              ],
            }),
          },
          ...reviewNodes(url, `${url}#service`, 2).map((r) => ({ type: "application/ld+json" as const, children: JSON.stringify(r) })),
        ]),
      ],
    };
  },
  component: EnMarriageVenuePage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">Not found</h1>
      <Link to="/en/marriage" className="mt-4 inline-block text-amber-300 underline">Back to marriage hub</Link>
          <QuotableStatsStrip cluster="marriage" locale="en" variant="light" />
    </main>
  ),
});

function EnMarriageVenuePage() {
  const { nationality, location } = Route.useLoaderData() as { nationality: string; location: string };
  const n = getMarriageNationality(nationality)!;
  const v = getMarriageVenue(location)!;
  const otherVenues = MARRIAGE_VENUES.filter((x) => x.slug !== location && x.region === v.region).slice(0, 6);
  const otherNats = MARRIAGE_NATIONALITIES.filter((x) => x.slug !== nationality && x.region === n.region).slice(0, 6);

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/en" className="hover:text-gold">Home</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/en/marriage" className="hover:text-gold">Marry in Thailand</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/en/marriage/$nationality" params={{ nationality }} className="hover:text-gold">{n.countryEn}</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{v.nameTh}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Heart className="h-3.5 w-3.5" /> Marriage · {n.countryEn} × {v.nameTh}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            <span className="mr-3 text-4xl">{n.flag}</span>
            Marry a {n.countryEn} citizen at {v.nameTh}
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            One-stop service — embassy escort to {n.singleStatusSource}, sworn Thai translation, MFA legalization, and amphur booking at {v.amphoe} ({v.region}).
          </p>

          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "Package from", v: `THB ${n.serviceFeeTh.toLocaleString()}` },
              { k: "Turnaround", v: n.turnaround },
              { k: "District office", v: v.nameTh },
              { k: "Rating", v: "4.9 / 2,147" },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{s.v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md transition hover:-translate-y-0.5 hover:bg-emerald-400">
              <MessageCircle className="h-4 w-4" /> Chat on LINE
            </a>
            <a href="tel:+66832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md transition hover:-translate-y-0.5">
              <Phone className="h-4 w-4" /> +66 83 249 4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable · AI Overview ready
          </span>
          NYC arranges complete marriage registration between {n.countryEn} citizens and Thai partners at {v.amphoe} ({v.nameTh}) — from THB {n.serviceFeeTh.toLocaleString()}, completed in {n.turnaround}. Includes embassy escort to {n.singleStatusSource}, sworn Thai translation, MFA legalization, amphur booking, and English/native-language interpreter on the wedding day.
        </blockquote>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Why {v.nameTh}?</h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {v.highlights.map((h) => (
              <li key={h} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 text-sm text-slate-800">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {h}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">{n.countryEn} partner documents</h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {n.topDocs.map((d) => (
              <li key={d} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 text-sm text-slate-800">
                <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {d}
              </li>
            ))}
          </ul>
          <p className="mt-4 text-sm text-slate-600">Country-specific note: {n.notes}</p>
        </section>

        <section className="mt-12 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">One-stop package</div>
          <h2 className="mt-1 font-display text-3xl font-extrabold text-navy-deep sm:text-4xl">
            Marry {n.countryEn} at {v.nameTh} from THB {n.serviceFeeTh.toLocaleString()}
          </h2>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Embassy escort + sworn translation + MFA + {v.amphoe} booking</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Express 3-day service (+THB 3,500)</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Court-approved interpreter (English / native language) (+THB 2,500)</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Kor.Ror.2 / Kor.Ror.3 translation + {n.apostilleOnly ? "Apostille" : "embassy legalization"} for use in {n.countryEn}</li>
          </ul>
          <div className="mt-6"><ContactCTA /></div>
        </section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Frequently asked questions</h2>
          <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-2 sm:p-3">
            <FaqAccordion items={[
              { q: `Can a ${n.countryEn} citizen register marriage at ${v.nameTh}?`, a: `Yes — ${v.amphoe} accepts foreign-Thai marriage registrations. NYC handles affidavit, translation, MFA, and books the appointment.` },
              { q: `How long does the process take?`, a: `${n.turnaround} for the standard pathway. Express 3-day service is +THB 3,500.` },
              { q: `Do we need an interpreter on the wedding day?`, a: `Most amphurs in Bangkok and tourist provinces have English-speaking officers, but Thai law requires that both parties understand what they sign. NYC provides a court-approved interpreter (English or native) for THB 2,500.` },
              { q: `Will the Thai marriage be recognised in ${n.countryEn}?`, a: n.apostilleOnly ? `Yes — translate the Kor.Ror.2 / Kor.Ror.3 and apostille at the Thai MFA. Directly accepted in ${n.countryEn}.` : `Yes — after MFA legalization, the document must be attested at the ${n.countryEn} embassy in Bangkok.` },
            ]} />
          </div>
        </section>

        {otherVenues.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Other district offices in {v.region}</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {otherVenues.map((o) => (
                <Link key={o.slug} to="/en/marriage/$nationality/in/$location" params={{ nationality, location: o.slug }}
                  className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md">
                  <MapPin className="h-5 w-5 text-amber-600" />
                  <div className="flex-1">
                    <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">{o.nameTh}</div>
                    <div className="mt-0.5 text-[11px] text-slate-500">{o.amphoe}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-amber-600" />
                </Link>
              ))}
            </div>
          </section>
        )}

        {otherNats.length > 0 && (
          <section className="mt-12">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Other nationalities at {v.nameTh}</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {otherNats.map((o) => (
                <Link key={o.slug} to="/en/marriage/$nationality/in/$location" params={{ nationality: o.slug, location }}
                  className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md">
                  <div className="text-2xl">{o.flag}</div>
                  <div className="flex-1">
                    <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">{o.countryEn}</div>
                    <div className="mt-0.5 text-[11px] text-slate-500">From THB {o.serviceFeeTh.toLocaleString()} · {o.turnaround}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      <TrustLayer variant="compact" />
          <QuotableStatsStrip cluster="marriage" locale="en" variant="light" />
    </main>
  );
}
